import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getOfficeRequirements } from '../lib/officeRequirements';
import { ChevronLeft, ChevronRight, Search, Users, Trophy, Gauge } from 'lucide-react';
import { 
  getStates,
  getStateData,
  getCountiesForState, 
  getCitiesForCounty, 
  getDistrictsForStateAndOffice,
  filterSuggestions
} from '../lib/formData';
import { ElectionDetailsStep } from '../components/ElectionDetailsStep';
import { CandidateInfoStep } from '../components/CandidateInfoStep';
import { saveCampaignData } from '../lib/database';
import type { CampaignData } from '../lib/types';
import { useCampaignStore } from '../lib/store/campaignStore';
import { supabase } from '../lib/supabase';

const competitivenessColors = {
  'very-high': 'bg-red-500',
  'high': 'bg-orange-500',
  'medium': 'bg-yellow-500',
  'low': 'bg-green-500'
};

export function FormFlow() {
  const location = useLocation();
  const navigate = useNavigate();
  const _setCampaignData = useCampaignStore(state => state);
  const setCampaignData = _setCampaignData?.setCampaignData
  const _campaignData = _setCampaignData?.campaignData
  const selectedOffice = location.state?.office || '';
  const requirements = getOfficeRequirements(selectedOffice);

  const [currentStep, setCurrentStep] = useState(1);
  const [shakeError, setShakeError] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [formData, setFormData] = useState({
    state: '',
    county: '',
    city: '',
    district: '',
  });
  const [selectedType, setSelectedType] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedStatus, setSelectedStatus] = useState('');

  const [stateInfo, setStateInfo] = useState<{ population: number; capital: string; region: string; competitive?: boolean } | null>(null);

  const [suggestions, setSuggestions] = useState({
    states: getStates(),
    counties: [],
    cities: [],
    districts: []
  });

  const [filteredSuggestions, setFilteredSuggestions] = useState({
    states: getStates(),
    counties: [],
    cities: [],
    districts: []
  });

  const [showSuggestions, setShowSuggestions] = useState({
    state: false,
    county: false,
    city: false,
    district: false
  });

  const inputRefs = {
    state: useRef<HTMLDivElement>(null),
    county: useRef<HTMLDivElement>(null),
    city: useRef<HTMLDivElement>(null),
    district: useRef<HTMLDivElement>(null)
  };

  const locationStepsCount = Object.values(requirements).filter(Boolean).length;
  const totalSteps = locationStepsCount + 6;
  const currentSection = currentStep <= locationStepsCount ? 'location' : 
                        currentStep === locationStepsCount + 1 ? 'election' : 'candidate';

  const getProgressPercentage = () => {
    let completedSteps = currentStep;
    
    if (currentSection === 'election') {
      completedSteps += locationStepsCount;
    } else if (currentSection === 'candidate') {
      completedSteps += locationStepsCount + 3;
    }
    
    return (completedSteps / totalSteps) * 100;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      Object.entries(inputRefs).forEach(([key, ref]) => {
        if (ref.current && !ref.current.contains(event.target as Node)) {
          setShowSuggestions(prev => ({ ...prev, [key]: false }));
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && currentSection === 'location') {
        handleNext();
      }
    };

    document.addEventListener('keypress', handleKeyPress);
    return () => document.removeEventListener('keypress', handleKeyPress);
  }, [currentStep, formData, currentSection]);

  useEffect(() => {
    if (formData.state) {
      const counties = getCountiesForState(formData.state);
      setSuggestions(prev => ({ ...prev, counties }));
      setFilteredSuggestions(prev => ({ ...prev, counties }));
      
      const stateData = getStateData(formData.state);
      setStateInfo(stateData || null);
    }
  }, [formData.state]);

  useEffect(() => {
    if (formData.state && formData.county) {
      const cities = getCitiesForCounty(formData.state, formData.county);
      setSuggestions(prev => ({ ...prev, cities }));
      setFilteredSuggestions(prev => ({ ...prev, cities }));
    }
  }, [formData.state, formData.county]);

  useEffect(() => {
    if (formData.state && selectedOffice) {
      const districts = getDistrictsForStateAndOffice(formData.state, selectedOffice);
      setSuggestions(prev => ({ ...prev, districts }));
      setFilteredSuggestions(prev => ({ ...prev, districts }));
    }
  }, [formData.state, selectedOffice]);

  const formatPopulation = (population: number): string => {
    if (population >= 1000000) {
      return `${(population / 1000000).toFixed(1)}M`;
    } else if (population >= 1000) {
      return `${(population / 1000).toFixed(1)}K`;
    }
    return population.toString();
  };

  const getCompetitiveness = (item: any): 'very-high' | 'high' | 'medium' | 'low' => {
    if (item.competitive === true) return 'very-high';
    if (item.population > 1000000) return 'high';
    if (item.population > 500000) return 'medium';
    return 'low';
  };

  const isStepValid = () => {
    const locationStepsCount = Object.values(requirements).filter(Boolean).length;
    const currentSection = currentStep <= locationStepsCount ? 'location' : 
                          currentStep === locationStepsCount + 1 ? 'election' : 'candidate';

    if (currentSection === 'location') {
      switch (currentStep) {
        case 1:
          return !!formData.state;
        case 2:
          return !!formData.county;
        case 3:
          return !!formData.city;
        case 4:
          return true;
        default:
          return true;
      }
    }
    return true;
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    let filtered;
    switch (field) {
      case 'state':
        filtered = filterSuggestions(suggestions.states, value);
        setFilteredSuggestions(prev => ({ ...prev, states: filtered }));
        break;
      case 'county':
        if (formData.state) {
          const counties = getCountiesForState(formData.state);
          filtered = filterSuggestions(counties, value, 'county');
          setFilteredSuggestions(prev => ({ ...prev, counties: filtered }));
        }
        break;
      case 'city':
        filtered = filterSuggestions(suggestions.cities, value, 'city');
        setFilteredSuggestions(prev => ({ ...prev, cities: filtered }));
        break;
      case 'district':
        filtered = filterSuggestions(suggestions.districts, value, 'district');
        setFilteredSuggestions(prev => ({ ...prev, districts: filtered }));
        break;
    }
    
    setShowSuggestions(prev => ({ ...prev, [field]: true }));
  };

  const handleSuggestionSelect = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setShowSuggestions(prev => ({ ...prev, [field]: false }));

    if (field === 'state') {
      setFormData(prev => ({ ...prev, county: '', city: '', district: '' }));
      const counties = getCountiesForState(value);
      setSuggestions(prev => ({ ...prev, counties }));
      setFilteredSuggestions(prev => ({ ...prev, counties }));
      
      const stateData = getStateData(value);
      setStateInfo(stateData || null);
      
      if (currentStep < totalSteps) {
        setCurrentStep(prev => prev + 1);
      }
    } else if (field === 'county') {
      setFormData(prev => ({ ...prev, city: '', district: '' }));
      const cities = getCitiesForCounty(formData.state, value);
      setSuggestions(prev => ({ ...prev, cities }));
      setFilteredSuggestions(prev => ({ ...prev, cities }));
      
      if (currentStep < totalSteps) {
        setCurrentStep(prev => prev + 1);
      }
    } else {
      if (currentStep < totalSteps) {
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const handleNext = () => {
    if (!isStepValid()) {
      setShakeError(true);
      setTimeout(() => setShakeError(false), 500);
      return;
    }

    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    } else {
      navigate('/');
    }
  };

  const handleComplete = async (candidateInfo) => {
    try {
      console.log('Submitting form data...');

      const campaignData: CampaignData = {
        office: selectedOffice,
        location_state: formData.state,
        location_county: formData.county,
        location_city: formData.city,
        location_district: formData.district,
        full_name: candidateInfo?.name || '',
        email: candidateInfo?.email || '',
        phone: candidateInfo?.phone || '',
        sms_consent: candidateInfo?.smsConsent || false,
        political_party: candidateInfo?.politicalParty || '',
        election_date: selectedDate || new Date(),
        election_type: selectedType,
        incumbent_status: selectedStatus
      };

      // if(!formData?.state) delete campaignData?.location_state

      const campaignDataLocally: CampaignData = {
        office: selectedOffice,
        location: {
          state: formData.state,
          county: formData.county,
          city: formData.city,
          district: formData.district,
        },
        candidateInfo: {
          ...candidateInfo
        },
        electionDate: selectedDate || new Date(),
        electionType: selectedType,
        incumbentStatus: selectedStatus
      };

      console.log('Saving campaign data:', campaignData);

      const success = await saveCampaignData(campaignData,campaignDataLocally);
      if (!success) {
        throw new Error('Failed to save campaign data');
      }
      setCampaignData(campaignDataLocally);
      navigate('/dashboard');
    } catch (error) {
      console.error('Form submission error:', error);
      // Still navigate to dashboard even if save fails - we have localStorage backup
      navigate('/dashboard');
    }
  };

  const handleElectionDetails = (electionData: { type: string; date: Date; status: string }) => {
    setSelectedType(electionData.type);
    setSelectedDate(electionData.date);
    setSelectedStatus(electionData.status);
    setCurrentStep(prev => prev + 1);
  };

  const renderSuggestionsList = (field: string) => {
    let items: any[] = [];
    let displayKey = '';

    switch (field) {
      case 'state':
        items = filteredSuggestions.states.map(state => ({
          name: state,
          ...getStateData(state)
        }));
        break;
      case 'county':
        items = filteredSuggestions.counties;
        displayKey = 'county';
        break;
      case 'city':
        items = filteredSuggestions.cities;
        displayKey = 'city';
        break;
      case 'district':
        items = filteredSuggestions.districts;
        displayKey = 'district';
        break;
    }

    if (!items.length) return null;

    return (
      <AnimatePresence>
        {showSuggestions[field] && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-10 w-full mt-2 bg-[#1a2236] border border-white/10 rounded-lg shadow-xl max-h-60 overflow-y-auto"
          >
            {items.map((item, index) => (
              <button
                key={index}
                className="w-full px-4 py-3 text-left text-white hover:bg-white/10 transition-colors"
                onClick={() => handleSuggestionSelect(field, displayKey ? item[displayKey] : item.name)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{displayKey ? item[displayKey] : item.name}</div>
                    {item.notes && (
                      <div className="text-sm text-gray-400">{item.notes}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 px-2 py-1 rounded bg-white/5">
                      <Gauge className="w-4 h-4" />
                      <div className={`w-16 h-1.5 rounded-full ${competitivenessColors[getCompetitiveness(item)]}`} />
                    </div>
                    <div className="flex items-center gap-1 text-gray-400">
                      <Users className="w-4 h-4" />
                      <span>{formatPopulation(item.population)}</span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  const renderLocationStep = () => {
    const steps = [
      requirements.needsState && (
        <motion.div
          key="state"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="space-y-6"
          ref={inputRefs.state}
        >
          <h2 className="text-3xl font-bold mb-8">Which state are you running in?</h2>
          <p className="text-gray-400 mb-8">
            This helps us understand your campaign's jurisdiction and applicable regulations.
          </p>
          <div className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={formData.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                onFocus={() => setShowSuggestions(prev => ({ ...prev, state: true }))}
                placeholder="Enter state name"
                className="w-full pl-10 pr-4 py-3 bg-white/10 rounded-lg text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#026fb9]"
                autoComplete="off"
              />
            </div>
            {renderSuggestionsList('state')}
          </div>
          {stateInfo && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 p-4 bg-white/5 rounded-lg"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">{formData.state}</h3>
                  <p className="text-gray-400">Capital: {stateInfo.capital}</p>
                  <p className="text-gray-400">Region: {stateInfo.region}</p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 px-2 py-1 rounded bg-white/5">
                    <Gauge className="w-4 h-4" />
                    <div className={`w-16 h-1.5 rounded-full ${competitivenessColors[getCompetitiveness(stateInfo)]}`} />
                  </div>
                  <div className="flex items-center gap-2 text-gray-400">
                    <Users className="w-5 h-5" />
                    <span className="text-lg font-semibold">{formatPopulation(stateInfo.population)}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
      ),
      requirements.needsCounty && (
        <motion.div
          key="county"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="space-y-6"
          ref={inputRefs.county}
        >
          <h2 className="text-3xl font-bold mb-8">Which county is your campaign in?</h2>
          <p className="text-gray-400 mb-8">
            County-level information helps us determine local campaign requirements and voter demographics.
          </p>
          <div className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={formData.county}
                onChange={(e) => handleInputChange('county', e.target.value)}
                onFocus={() => {
                  if (formData.state) {
                    const counties = getCountiesForState(formData.state);
                    setFilteredSuggestions(prev => ({ ...prev, counties }));
                    setShowSuggestions(prev => ({ ...prev, county: true }));
                  }
                }}
                placeholder="Enter county name"
                className="w-full pl-10 pr-4 py-3 bg-white/10 rounded-lg text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#026fb9]"
                autoComplete="off"
              />
            </div>
            {renderSuggestionsList('county')}
          </div>
        </motion.div>
      ),
      requirements.needsCity && (
        <motion.div
          key="city"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="space-y-6"
          ref={inputRefs.city}
        >
          <h2 className="text-3xl font-bold mb-8">Which city/town are you running in?</h2>
          <p className="text-gray-400 mb-8">
            City information helps us calculate local voter demographics and campaign costs.
          </p>
          <div className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                onFocus={() => {
                  if (formData.state && formData.county) {
                    const cities = getCitiesForCounty(formData.state, formData.county);
                    setFilteredSuggestions(prev => ({ ...prev, cities }));
                    setShowSuggestions(prev => ({ ...prev, city: true }));
                  }
                }}
                placeholder="Enter city name"
                className="w-full pl-10 pr-4 py-3 bg-white/10 rounded-lg text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#026fb9]"
                autoComplete="off"
              />
            </div>
            {renderSuggestionsList('city')}
          </div>
        </motion.div>
      ),
      requirements.needsDistrict && (
        <motion.div
          key="district"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="space-y-6"
          ref={inputRefs.district}
        >
          <h2 className="text-3xl font-bold mb-8">What's your district number?</h2>
          <p className="text-gray-400 mb-8">
            This helps us narrow down your specific constituency and voting demographics.
          </p>
          <div className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={formData.district}
                onChange={(e) => handleInputChange('district', e.target.value)}
                onFocus={() => {
                  if (formData.state && selectedOffice) {
                    const districts = getDistrictsForStateAndOffice(formData.state, selectedOffice);
                    setFilteredSuggestions(prev => ({ ...prev, districts }));
                    setShowSuggestions(prev => ({ ...prev, district: true }));
                  }
                }}
                placeholder="Enter district/ward name or number"
                className="w-full pl-10 pr-4 py-3 bg-white/10 rounded-lg text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#026fb9]"
                autoComplete="off"
              />
            </div>
            {renderSuggestionsList('district')}
          </div>
        </motion.div>
      )
    ].filter(Boolean);

    return steps[currentStep - 1] || null;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-6">
              Campaign Details for {selectedOffice}
            </h1>
            <div className="h-1 w-full bg-white/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary transition-all duration-300 rounded-full"
                style={{ width: `${getProgressPercentage()}%` }}
              />
            </div>
          </div>

          {currentSection === 'location' && renderLocationStep()}
          {currentSection === 'election' && (
            <ElectionDetailsStep 
              onNext={handleElectionDetails}
              onBack={handleBack}
              progressPercentage={getProgressPercentage()}
            />
          )}
          {currentSection === 'candidate' && (
            <CandidateInfoStep 
              onNext={handleComplete} 
              onBack={handleBack}
              progressPercentage={getProgressPercentage()}
            />
          )}

          {currentSection === 'location' && (
            <div className="mt-12 flex justify-between">
              <button
                onClick={handleBack}
                className="flex items-center px-6 py-3 text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 mr-2" />
                Back
              </button>
              <motion.button
                onClick={handleNext}
                animate={shakeError ? {
                  x: [-10, 10, -10, 10, 0],
                  transition: { duration: 0.4 }
                } : {}}
                className="flex items-center px-6 py-3 bg-[#026fb9] text-white rounded-lg hover:bg-[#026fb9]/90 transition-colors"
              >
                Next
                <ChevronRight className="w-5 h-5 ml-2" />
              </motion.button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}